/**
 * Institution Query Keys
 *
 * Centralized query key factory for all institution-related queries.
 * Supports global institution search and matter-specific institution data.
 */

import type { InstitutionFilters } from './types';

/**
 * Institution query key factory
 *
 * @example
 * ```typescript
 * // Search institutions globally
 * useQuery({ queryKey: institutionKeys.search('commonwealth'), queryFn: ... })
 *
 * // Get matter's institutions
 * useQuery({ queryKey: institutionKeys.forMatter(matterId), queryFn: ... })
 * ```
 */
export const institutionKeys = {
  /**
   * Root key for all institution queries
   */
  all: ['institutions'] as const,

  /**
   * Base key for institution lists
   */
  lists: () => [...institutionKeys.all, 'list'] as const,

  /**
   * Filtered institution list (global)
   * @param filters - Optional filters
   */
  list: (filters?: InstitutionFilters) =>
    [...institutionKeys.lists(), filters] as const,

  /**
   * Institution search
   * @param query - Search query string
   */
  search: (query: string) => [...institutionKeys.all, 'search', query] as const,

  /**
   * Base key for institution details
   */
  details: () => [...institutionKeys.all, 'detail'] as const,

  /**
   * Specific institution detail
   * @param institutionId - The institution ID
   */
  detail: (institutionId: number) =>
    [...institutionKeys.details(), institutionId] as const,

  /**
   * Institution types (reference data)
   */
  types: () => [...institutionKeys.all, 'types'] as const,

  /**
   * Specific institution type
   * @param slug - The type slug
   */
  type: (slug: string) => [...institutionKeys.types(), slug] as const,

  /**
   * Matter-specific institution data
   * @param matterId - The matter ID
   */
  forMatter: (matterId: number) => ['matter-institutions', matterId] as const,

  /**
   * Institution status for a matter
   * @param matterId - The matter ID
   * @param institutionId - The institution ID
   */
  status: (matterId: number, institutionId?: number) =>
    institutionId
      ? ([
          ...institutionKeys.forMatter(matterId),
          'status',
          institutionId
        ] as const)
      : ([...institutionKeys.forMatter(matterId), 'status'] as const),

  /**
   * Institution correspondence
   * @param matterId - The matter ID
   * @param institutionId - The institution ID
   */
  correspondence: (matterId: number, institutionId?: number) =>
    institutionId
      ? ([
          ...institutionKeys.forMatter(matterId),
          'correspondence',
          institutionId
        ] as const)
      : ([...institutionKeys.forMatter(matterId), 'correspondence'] as const),

  /**
   * Institution forms
   * @param matterId - The matter ID
   * @param institutionId - The institution ID
   */
  forms: (matterId: number, institutionId: number) =>
    ['institution-forms', matterId, institutionId] as const,

  /**
   * All forms for a matter (aggregated)
   * @param matterId - The matter ID
   */
  allForms: (matterId: number) => ['institution-forms', matterId] as const
} as const;
